/* Ouderportaal: minimalistische ouder-view met eigen kinderen, facturen,
   schriftjes en berichten. */
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
// URL-helper: backend geeft photo.url terug als "/api/photos/{id}/file".
// API-const bevat al "/api" → gewoon `${BACKEND}${photo.url}` gebruiken.
const BACKEND_ROOT = API.replace(/\/api$/, "");
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Baby, Wallet, MessageSquare, BookOpen, LogOut, Smile, Meh, Frown, FileDown, Utensils, Bed, Clock, LogIn, Receipt, Calculator, Send, Camera, X, AlertCircle, CalendarDays, Sparkles } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import OuderHighlightsCard from "@/components/OuderHighlightsCard";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatCurrency, pluralNL, localToday } from "@/lib/format";
import { MessageBubble } from "@/pages/CommunicatiePage";
import OuderSplash from "@/components/OuderSplash";
import ExtraOpvangdagAanvraag from "@/components/ExtraOpvangdagAanvraag";

const STEMMING_ICON = { blij: Smile, neutraal: Meh, verdrietig: Frown, boos: Frown, moe: Meh };
const STATUS_TONE = {
  concept: "bg-neutral-100 text-neutral-700 border-neutral-200",
  verzonden: "bg-blue-50 text-blue-700 border-blue-200",
  betaald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  overtijd: "bg-red-50 text-red-700 border-red-200",
  gecrediteerd: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function OuderPortaalPage() {
  const { user, logout, status } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedKid, setSelectedKid] = useState(null);
  const [schriftjes, setSchriftjes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [dagLog, setDagLog] = useState([]);
  const [presentieToday, setPresentieToday] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [kotData, setKotData] = useState(null);
  const [kotYear, setKotYear] = useState(new Date().getFullYear());
  const [ophaaltijd, setOphaaltijd] = useState("");
  const [showSickDialog, setShowSickDialog] = useState(false);
  const [afmeldBusy, setAfmeldBusy] = useState(false);

  useEffect(() => {
    if (status !== "authed" || user?.role !== "ouder") return;
    (async () => {
      try {
        const [ov, inv, msg] = await Promise.all([
          api.get("/portaal/overview"),
          api.get("/portaal/facturen"),
          api.get("/mentorbericht"),
        ]);
        setData(ov.data); setInvoices(inv.data); setMessages(msg.data);
        if (ov.data.kinderen?.length) setSelectedKid(ov.data.kinderen[0]);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [status, user]);

  // Zet PWA-manifest naar de "ouder"-variant zodat een geïnstalleerd
  // Ouderportaal-app-icoon náást de admin-app op het startscherm past.
  useEffect(() => {
    if (status !== "authed" || user?.role !== "ouder") return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const orgParam = data?.organization_id ? `&org_id=${encodeURIComponent(data.organization_id)}` : "";
    const manifestHref = `${backend}/api/manifest.json?variant=ouder${orgParam}`;
    let link = document.querySelector('link[rel="manifest"]');
    if (link) link.setAttribute("href", manifestHref);
    // theme-color: match ouder-accent (uit tenant branding.secundair)
    if (data?.tenant?.logo_url) {
      const iconUrl = data.tenant.logo_url.startsWith("http")
        ? data.tenant.logo_url : `${backend}${data.tenant.logo_url}`;
      let apple = document.querySelector('link[rel="apple-touch-icon"]');
      if (!apple) {
        apple = document.createElement("link");
        apple.setAttribute("rel", "apple-touch-icon");
        document.head.appendChild(apple);
      }
      apple.setAttribute("href", iconUrl);
    }
  }, [status, user, data]);

  // KOT-jaaroverzicht laden
  useEffect(() => {
    if (status !== "authed" || user?.role !== "ouder") return;
    api.get("/portaal/kot", { params: { year: kotYear } })
      .then(r => setKotData(r.data))
      .catch(() => setKotData(null));
  }, [status, user, kotYear]);

  useEffect(() => {
    if (!selectedKid) return;
    api.get(`/portaal/kind/${selectedKid.id}/schriftjes`).then(r => setSchriftjes(r.data));
    api.get(`/daglog`, { params: { kind_id: selectedKid.id, datum: localToday() } }).then(r => setDagLog(r.data)).catch(() => setDagLog([]));
    api.get(`/portaal/kind/${selectedKid.id}/presentie`).then(r => {
      setPresentieToday(r.data);
      setOphaaltijd(r.data?.vertrek || "");
    }).catch(() => { setPresentieToday(null); setOphaaltijd(""); });
    api.get(`/portaal/kind/${selectedKid.id}/photos`, { params: { limit: 60 } })
      .then(r => setPhotos(r.data || []))
      .catch(() => setPhotos([]));
  }, [selectedKid]);

  const afmelden = async () => {
    if (!selectedKid) return;
    if (!ophaaltijd || !/^([01]\d|2[0-3]):[0-5]\d$/.test(ophaaltijd)) {
      toast.error("Vul een geldige ophaaltijd in (HH:MM) — afmelden kan alleen met een ophaaltijd");
      return;
    }
    setAfmeldBusy(true);
    try {
      const r = await api.post(`/portaal/presentie/${selectedKid.id}/afmelden`, { vertrek: ophaaltijd });
      setPresentieToday(r.data.presentie);
      toast.success(`${selectedKid.voornaam} is afgemeld om ${ophaaltijd}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Afmelden mislukt");
    } finally {
      setAfmeldBusy(false);
    }
  };

  if (status === "loading") return <div className="min-h-screen flex items-center justify-center bg-[color:var(--bone)]">Laden…</div>;
  if (status === "anon") return <Navigate to="/login" replace />;
  if (user?.role !== "ouder") return <Navigate to="/app" replace />;

  const markRead = async (id) => {
    try { await api.post(`/mentorbericht/${id}/lees`); const r = await api.get("/mentorbericht"); setMessages(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadUBL = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/ubl`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/xml" }));
      const a = document.createElement("a"); a.href = url; a.download = `${inv.invoice_number}.ubl.xml`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadPDF = async (inv) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `${inv.invoice_number}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const initials = user?.name?.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase() || "O";

  return (
    <div className="min-h-screen bg-[color:var(--bone)] subtle-grain">
      <OuderSplash
        kinderen={data?.kinderen || []}
        ouderNaam={user?.name}
      />
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-md flex items-center justify-center shrink-0" style={{ background: "var(--sage)" }}>
              <Baby className="h-4 w-4 sm:h-5 sm:w-5 text-white" strokeWidth={2.4} />
            </div>
            <div className="min-w-0">
              <div className="font-display text-sm sm:text-[15px] leading-tight truncate">Webster<span className="text-[color:var(--terracotta)]">.</span></div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ouderportaal</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium truncate max-w-[180px]">{user?.name}</div>
              <div className="text-[11px] text-neutral-500 truncate max-w-[180px]">{user?.email}</div>
            </div>
            <Avatar className="h-8 w-8 sm:h-9 sm:w-9 border border-neutral-200">
              <AvatarFallback className="bg-[color:var(--terracotta)] text-white text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <Button variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/login"); }} data-testid="portaal-logout">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-24 lg:pb-8">
        {!data ? (
          <div className="text-sm text-neutral-500">Laden…</div>
        ) : (
          <>
            <div className="mb-6 sm:mb-8">
              <h1 className="font-display text-2xl sm:text-3xl tracking-tight" data-testid="portaal-welcome">
                Welkom, {user?.name?.split(" ")[0]}
              </h1>
              <p className="text-neutral-500 mt-1 text-sm">Overzicht van uw kinderen, facturen, schriftje en berichten.</p>
            </div>

            {/* Highlights welkomstscherm — nieuwste foto's + schriftjes over alle eigen kinderen */}
            <OuderHighlightsCard
              onOpenPhoto={(p) => setLightbox(p)}
              onNavigate={(path) => navigate(path)}
            />

            {/* Vrijdag Weekstory-banner — alleen vr, za, zo zichtbaar */}
            {[5, 6, 0].includes(new Date().getDay()) && (
              <button
                onClick={() => navigate("/portaal/weekstory")}
                className="w-full mb-4 relative overflow-hidden rounded-xl p-4 text-white flex items-center gap-3 shadow-md hover:shadow-lg transition-shadow"
                style={{ background: "linear-gradient(135deg, #3a5a40, #6b8e5a 55%, #c9a86a)" }}
                data-testid="portaal-weekstory-banner"
              >
                <div className="w-11 h-11 rounded-full bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="flex-1 text-left">
                  <div className="text-xs uppercase tracking-widest opacity-80">Deze week</div>
                  <div className="text-sm font-semibold">Bekijk de foto-terugblik</div>
                  <div className="text-[11px] opacity-80">Top 3 foto's + hoogtepunten in een korte story</div>
                </div>
                <div className="text-xs bg-white/20 backdrop-blur rounded-full px-2 py-1">Start →</div>
              </button>
            )}

            {/* Quick-nav naar mobile-first ouder-pagina's */}
            <div className="grid grid-cols-5 gap-2 mb-6" data-testid="portaal-quicknav">
              <button onClick={() => navigate("/portaal/dagverslag")} className="flex flex-col items-center gap-1 rounded-lg border border-neutral-200 bg-white p-3 hover:border-emerald-300 hover:bg-emerald-50 transition-colors" data-testid="quicknav-dagverslag">
                <BookOpen className="h-5 w-5 text-[color:var(--sage)]" />
                <span className="text-[11px] font-medium text-neutral-700">Dagverslag</span>
              </button>
              <button onClick={() => navigate("/portaal/berichten")} className="flex flex-col items-center gap-1 rounded-lg border border-neutral-200 bg-white p-3 hover:border-emerald-300 hover:bg-emerald-50 transition-colors relative" data-testid="quicknav-berichten">
                <MessageSquare className="h-5 w-5 text-[color:var(--sage)]" />
                <span className="text-[11px] font-medium text-neutral-700">Berichten</span>
                {data.ongelezen_berichten > 0 && (
                  <span className="absolute top-1 right-2 text-[10px] font-semibold bg-red-500 text-white rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{data.ongelezen_berichten}</span>
                )}
              </button>
              <button onClick={() => navigate("/portaal/planning")} className="flex flex-col items-center gap-1 rounded-lg border border-neutral-200 bg-white p-3 hover:border-emerald-300 hover:bg-emerald-50 transition-colors" data-testid="quicknav-planning">
                <CalendarDays className="h-5 w-5 text-[color:var(--sage)]" />
                <span className="text-[11px] font-medium text-neutral-700">Planning</span>
              </button>
              <button onClick={() => navigate("/portaal/facturen")} className="flex flex-col items-center gap-1 rounded-lg border border-neutral-200 bg-white p-3 hover:border-emerald-300 hover:bg-emerald-50 transition-colors relative" data-testid="quicknav-facturen">
                <Wallet className="h-5 w-5 text-[color:var(--sage)]" />
                <span className="text-[11px] font-medium text-neutral-700">Facturen</span>
                {data.aantal_open_facturen > 0 && (
                  <span className="absolute top-1 right-2 text-[10px] font-semibold bg-red-500 text-white rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{data.aantal_open_facturen}</span>
                )}
              </button>
              <button onClick={() => navigate("/portaal/album")} className="flex flex-col items-center gap-1 rounded-lg border border-neutral-200 bg-white p-3 hover:border-emerald-300 hover:bg-emerald-50 transition-colors" data-testid="quicknav-album">
                <Camera className="h-5 w-5 text-[color:var(--sage)]" />
                <span className="text-[11px] font-medium text-neutral-700">Album</span>
              </button>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <Card className="p-5 bg-white shadow-none border-l-4 border-l-[color:var(--sage)]" data-testid="portaal-kids-count">
                <div className="text-xs uppercase tracking-wider text-neutral-500">Uw kinderen</div>
                <div className="font-display text-3xl mt-2">{data.kinderen.length}</div>
              </Card>
              <Card className={`p-5 bg-white shadow-none border-l-4 ${data.openstaand > 0 ? "border-l-red-500" : "border-l-emerald-500"}`}>
                <div className="text-xs uppercase tracking-wider text-neutral-500">Openstaand</div>
                <div className={`font-display text-3xl mt-2 ${data.openstaand > 0 ? "text-red-700" : "text-emerald-700"}`}>
                  {formatCurrency(data.openstaand)}
                </div>
                <div className="text-xs text-neutral-500 mt-1">
                  {data.aantal_open_facturen === 0
                    ? "Alles betaald — bedankt!"
                    : `${pluralNL(data.aantal_open_facturen, "openstaande factuur", "openstaande facturen")}`}
                </div>
              </Card>
              <Card className="p-5 bg-white shadow-none border-l-4 border-l-[color:var(--terracotta)]">
                <div className="text-xs uppercase tracking-wider text-neutral-500">Nieuwe berichten</div>
                <div className="font-display text-3xl mt-2">{data.ongelezen_berichten}</div>
              </Card>
            </div>

            {/* Kids selector */}
            {data.kinderen.length > 0 && (
              <div className="mb-6">
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Selecteer een kind</div>
                <div className="flex flex-wrap gap-2">
                  {data.kinderen.map(k => (
                    <button
                      key={k.id}
                      onClick={() => setSelectedKid(k)}
                      data-testid={`portaal-kind-${k.id}`}
                      className={`flex items-center gap-3 rounded-md border px-3 py-2 transition-colors ${selectedKid?.id === k.id ? "border-[color:var(--sage)] bg-[color:var(--sage-100)]" : "border-neutral-200 hover:bg-neutral-100"}`}>
                      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[color:var(--sage)] text-white text-xs font-semibold">
                        {k.voornaam[0]}{k.achternaam[0]}
                      </div>
                      <div className="text-left">
                        <div className="text-sm font-medium">{k.voornaam} {k.achternaam}</div>
                        <div className="text-xs text-neutral-500">geb. {new Date(k.geboortedatum).toLocaleDateString('nl-NL')}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Schriftje voor geselecteerd kind */}
              <div className="lg:col-span-2">
                {/* Aanwezigheid vandaag + ophalen */}
                {selectedKid && (
                  <div className="mb-6" data-testid="portaal-presentie-card">
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="h-4 w-4 text-[color:var(--sage)]" />
                      <h2 className="font-display text-xl tracking-tight">Vandaag op de opvang</h2>
                    </div>
                    <Card className="p-4 sm:p-5 bg-white shadow-none">
                      {!presentieToday && (
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid="portaal-presentie-none">
                          <div className="text-sm text-neutral-500">
                            {selectedKid.voornaam} is vandaag nog niet aangekomen op de opvang.
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowSickDialog(true)}
                            data-testid="portaal-meld-ziek-btn"
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          >
                            <AlertCircle className="h-4 w-4 mr-1" />
                            Ziek of afwezig melden
                          </Button>
                        </div>
                      )}
                      {presentieToday && !presentieToday.absent_reason && (
                        <div className="mt-3 pt-3 border-t border-neutral-100">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowSickDialog(true)}
                            data-testid="portaal-plan-absent-btn"
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                          >
                            <AlertCircle className="h-4 w-4 mr-1" />
                            Vakantie of afwezigheid plannen
                          </Button>
                        </div>
                      )}
                      {presentieToday && presentieToday.absent_reason && (
                        <div className="text-sm bg-amber-50 border border-amber-200 rounded-md p-3 flex items-center gap-2 text-amber-800" data-testid="portaal-absent-info">
                          <AlertCircle className="h-4 w-4 shrink-0" />
                          <span>{selectedKid.voornaam} is vandaag afgemeld ({presentieToday.absent_reason}).</span>
                        </div>
                      )}
                      {presentieToday && (
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex flex-wrap items-center gap-4">
                            <div>
                              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Brengtijd</div>
                              <div className="text-lg font-semibold text-[color:var(--sage)] flex items-center gap-1" data-testid="portaal-brengtijd">
                                <LogIn className="h-4 w-4" />
                                {presentieToday.aankomst || "—"}
                              </div>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Ophaaltijd</div>
                              <div className="text-lg font-semibold text-[color:var(--terracotta)] flex items-center gap-1" data-testid="portaal-ophaaltijd">
                                <LogOut className="h-4 w-4" />
                                {presentieToday.vertrek || <span className="text-neutral-400">nog niet opgehaald</span>}
                              </div>
                            </div>
                            <Badge variant="outline"
                              className={presentieToday.aanwezig
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-neutral-100 text-neutral-600 border-neutral-200"}>
                              {presentieToday.aanwezig ? "Aanwezig" : "Afgemeld"}
                            </Badge>
                          </div>
                          {presentieToday.aanwezig && (
                            <div className="flex items-end gap-2">
                              <div>
                                <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">
                                  Ophaaltijd invoeren
                                </label>
                                <Input
                                  type="time"
                                  value={ophaaltijd}
                                  onChange={(e) => setOphaaltijd(e.target.value)}
                                  data-testid="portaal-ophaaltijd-input"
                                  className="h-9 w-[110px]"
                                />
                              </div>
                              <Button
                                size="sm"
                                onClick={afmelden}
                                disabled={afmeldBusy}
                                data-testid="portaal-afmelden-btn"
                                style={{ background: "var(--terracotta)" }}
                                className="h-9 text-white"
                                title="Afmelden vereist een ophaaltijd"
                              >
                                <LogOut className="h-4 w-4 mr-1" />
                                {afmeldBusy ? "Bezig…" : "Afmelden"}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="text-[11px] text-neutral-500 mt-3">
                        Afmelden kan alleen als een ophaaltijd is ingevoerd. De brengtijd is automatisch geregistreerd door de leidster en kan bij de locatie worden aangepast.
                      </div>
                    </Card>
                    <ExtraOpvangdagAanvraag kindId={selectedKid.id} kindNaam={selectedKid.voornaam} />
                  </div>
                )}

                {dagLog.length > 0 && presentieToday?.aankomst && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Utensils className="h-4 w-4 text-[color:var(--terracotta)]" />
                      <h2 className="font-display text-xl tracking-tight">Vandaag — maaltijden, slaap & meer</h2>
                    </div>
                    <Card className="p-4 bg-white shadow-none" data-testid="portaal-daglog">
                      <div className="space-y-1">
                        {dagLog.map(e => {
                          const meta = {
                            maaltijd: { icon: Utensils, color: "text-amber-700 bg-amber-50", label: "Maaltijd" },
                            slaap_start: { icon: Bed, color: "text-blue-700 bg-blue-50", label: "Slapen" },
                            slaap_eind: { icon: Bed, color: "text-emerald-700 bg-emerald-50", label: "Wakker" },
                            luier: { icon: Baby, color: "text-purple-700 bg-purple-50", label: "Luier" },
                            toilet: { icon: Baby, color: "text-purple-700 bg-purple-50", label: "Toilet" },
                            activiteit: { icon: BookOpen, color: "text-[color:var(--sage)] bg-[color:var(--sage-100)]", label: "Activiteit" },
                            notitie: { icon: BookOpen, color: "text-neutral-700 bg-neutral-100", label: "Notitie" },
                          }[e.type] || { icon: BookOpen, color: "text-neutral-700 bg-neutral-100", label: e.type };
                          const Ic = meta.icon;
                          return (
                            <div key={e.id} className="flex items-center gap-3 py-2" data-testid={`portaal-daglog-${e.id}`}>
                              <span className="font-mono text-sm text-neutral-500 w-14">{e.tijdstip.slice(11, 16)}</span>
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${meta.color}`}>
                                <Ic className="h-4 w-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-neutral-800">{meta.label}</div>
                                {e.details && <div className="text-xs text-neutral-500 truncate">{e.details}</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                  </div>
                )}

                {/* Weekplan-knop — thema, uitstapjes, menu */}
                {selectedKid && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    <button
                      onClick={() => navigate("/portaal/weekplan")}
                      data-testid="portaal-open-weekplan"
                      className="text-left"
                    >
                      <Card className="p-4 bg-gradient-to-br from-[color:var(--sage-100)]/40 to-white shadow-none hover:from-[color:var(--sage-100)]/60 transition-colors border border-[color:var(--sage-100)] h-full">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[color:var(--sage)] text-white shrink-0">
                            <CalendarDays className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-neutral-800 text-sm">Weekplan van de opvang</div>
                            <div className="text-[11px] text-neutral-500">Thema, uitstapjes en menu</div>
                          </div>
                          <div className="text-[color:var(--sage)] text-lg">→</div>
                        </div>
                      </Card>
                    </button>
                    <button
                      onClick={() => navigate("/portaal/portfolio")}
                      data-testid="portaal-open-portfolio"
                      className="text-left"
                    >
                      <Card className="p-4 bg-gradient-to-br from-amber-100/40 to-white shadow-none hover:from-amber-100/60 transition-colors border border-amber-100 h-full">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-md flex items-center justify-center bg-amber-500 text-white shrink-0">
                            <BookOpen className="h-5 w-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-neutral-800 text-sm">Portfolio {selectedKid.voornaam}</div>
                            <div className="text-[11px] text-neutral-500">Jaaroverzicht — print als herinnering</div>
                          </div>
                          <div className="text-amber-500 text-lg">→</div>
                        </div>
                      </Card>
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Camera className="h-4 w-4 text-[color:var(--terracotta)]" />
                    <h2 className="font-display text-xl tracking-tight">Foto's {selectedKid && `— ${selectedKid.voornaam}`}</h2>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/portaal/album")}
                    data-testid="portaal-open-album"
                    className="text-[color:var(--terracotta)] hover:bg-[color:var(--terracotta)]/5"
                  >
                    Bekijk album →
                  </Button>
                </div>
                <Card className="p-4 bg-white shadow-none mb-6" data-testid="portaal-photos">
                  {selectedKid && !selectedKid.toestemming_foto ? (
                    <div className="text-sm text-neutral-500">
                      Foto's zijn uitgeschakeld voor {selectedKid.voornaam}. Zet toestemming aan
                      via <span className="font-medium">Meer → Toestemmingen</span>.
                    </div>
                  ) : photos.length === 0 ? (
                    <div className="text-sm text-neutral-500">Nog geen foto's van {selectedKid?.voornaam}.</div>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {photos.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => setLightbox(p)}
                          className="group relative aspect-square rounded-md overflow-hidden bg-neutral-100 border border-neutral-200 hover:ring-2 hover:ring-[color:var(--terracotta)]/60 transition"
                          data-testid={`portaal-photo-${p.id}`}
                          title={`${new Date(p.created_at).toLocaleDateString('nl-NL')} · ${p.author_name || ''}`}
                        >
                          <img
                            src={`${BACKEND_ROOT}${p.url}`}
                            alt={`Foto ${new Date(p.created_at).toLocaleDateString('nl-NL')}`}
                            loading="lazy"
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/60 to-transparent text-[10px] text-white leading-tight text-left opacity-0 group-hover:opacity-100 transition">
                            <div>{new Date(p.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}</div>
                            {p.author_name && <div className="truncate">{p.author_name}</div>}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </Card>

                <div className="flex items-center gap-2 mb-3">
                  <BookOpen className="h-4 w-4 text-[color:var(--sage)]" />
                  <h2 className="font-display text-xl tracking-tight">Schriftje {selectedKid && `— ${selectedKid.voornaam}`}</h2>
                </div>
                <div className="space-y-3">
                  {schriftjes.length === 0 && (
                    <Card className="p-6 bg-white shadow-none text-center text-sm text-neutral-500">
                      Nog geen schriftje-entries.
                    </Card>
                  )}
                  {schriftjes.map(s => {
                    const St = STEMMING_ICON[s.stemming] || Smile;
                    // Match foto's op datum + kind — geen backend-koppeling nodig,
                    // we hebben beide lijsten al binnen.
                    const dayPhotos = photos.filter(p =>
                      (p.created_at || "").slice(0, 10) === s.datum &&
                      (p.kind_ids || []).includes(s.kind_id)
                    );
                    return (
                      <Card key={s.id} className="p-5 bg-white shadow-none" data-testid={`portaal-schriftje-${s.id}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full flex items-center justify-center bg-[color:var(--sage-100)] text-[color:var(--sage)]">
                            <St className="h-5 w-5" />
                          </div>
                          <div>
                            <div className="text-sm font-medium">{new Date(s.datum).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
                            <div className="text-xs text-neutral-500 capitalize">{s.stemming} · door {s.author_name}</div>
                          </div>
                        </div>
                        <p className="text-sm text-neutral-700 mb-2">{s.activiteiten}</p>
                        <div className="grid grid-cols-3 gap-2 text-xs text-neutral-600 mt-2">
                          {s.eten && <div>🍽 {s.eten}</div>}
                          {s.slaap && <div>😴 {s.slaap}</div>}
                          {s.verschoning_luier > 0 && <div>👶 {s.verschoning_luier}x</div>}
                        </div>
                        {s.hoogtepunt && (
                          <div className="mt-3 p-3 rounded-md bg-[color:var(--sage-100)]/50 border border-[color:var(--sage-100)] text-sm">
                            <span className="font-semibold text-[color:var(--sage)]">Hoogtepunt: </span>{s.hoogtepunt}
                          </div>
                        )}
                        {dayPhotos.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-neutral-100" data-testid={`schriftje-photos-${s.id}`}>
                            <div className="flex items-center gap-1.5 text-[11px] text-neutral-500 mb-2">
                              <Camera className="h-3 w-3" />
                              {dayPhotos.length === 1 ? "Foto van deze dag" : `${dayPhotos.length} foto's van deze dag`}
                            </div>
                            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                              {dayPhotos.map(p => (
                                <button
                                  key={p.id}
                                  type="button"
                                  onClick={() => setLightbox(p)}
                                  className="relative shrink-0 rounded-md overflow-hidden border border-neutral-200 hover:ring-2 hover:ring-[color:var(--terracotta)]/60 transition"
                                  data-testid={`schriftje-photo-${p.id}`}
                                  title={p.caption || undefined}
                                >
                                  <img
                                    src={`${BACKEND_ROOT}${p.url}`}
                                    alt={p.caption || "foto"}
                                    loading="lazy"
                                    className="h-20 w-20 object-cover"
                                  />
                                  {p.caption && (
                                    <div className="absolute bottom-0 left-0 right-0 px-1.5 py-0.5 bg-gradient-to-t from-black/70 to-transparent text-[9px] text-white truncate text-left">
                                      {p.caption}
                                    </div>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Sidebar: facturen + KOT + berichten */}
              <div className="space-y-6">
                {kotData && kotData.kinderen.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Calculator className="h-4 w-4 text-[color:var(--sage)]" />
                      <h2 className="font-display text-xl tracking-tight">KOT-uren {kotYear}</h2>
                    </div>
                    <div className="flex items-center gap-2 mb-3 text-xs">
                      <button onClick={() => setKotYear(y => y - 1)} data-testid="kot-year-prev"
                        className="px-2 py-1 rounded border border-neutral-200 hover:bg-neutral-50">‹</button>
                      <span className="font-mono">{kotYear}</span>
                      <button onClick={() => setKotYear(y => y + 1)} data-testid="kot-year-next"
                        className="px-2 py-1 rounded border border-neutral-200 hover:bg-neutral-50">›</button>
                      <div className="flex-1" />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            const r = await api.get("/portaal/kot/pdf", {
                              params: { year: kotYear },
                              responseType: "blob",
                            });
                            const blob = new Blob([r.data], { type: "application/pdf" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `KOT-jaaropgave-${kotYear}.pdf`;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            setTimeout(() => URL.revokeObjectURL(url), 2000);
                          } catch (e) {
                            toast.error("KOT-jaaropgave kon niet worden gedownload");
                          }
                        }}
                        data-testid="kot-pdf-btn"
                        className="h-7 border-[color:var(--sage)] text-[color:var(--sage)] hover:bg-[color:var(--sage)]/5 text-xs"
                      >
                        <FileDown className="h-3 w-3 mr-1" /> PDF-jaaropgave
                      </Button>
                    </div>
                    <Card className="p-3 bg-white shadow-none" data-testid="portaal-kot">
                      {kotData.kinderen.map(k => (
                        <div key={k.kind_id} className="mb-3 last:mb-0" data-testid={`kot-kind-${k.kind_id}`}>
                          <div className="flex items-center justify-between text-sm">
                            <div className="font-medium">{k.kind_naam}</div>
                            <div className="font-mono text-[color:var(--sage)]">{k.totaal_uren.toLocaleString('nl-NL', { minimumFractionDigits: 2 })} uur</div>
                          </div>
                          <div className="grid grid-cols-4 gap-1 mt-1.5">
                            {[1, 2, 3, 4].map(q => (
                              <div key={q} className="text-center p-1 rounded bg-neutral-50 border border-neutral-100">
                                <div className="text-[9px] text-neutral-500 uppercase">Q{q}</div>
                                <div className="text-[11px] font-mono">{k.per_kwartaal[q].toLocaleString('nl-NL', { minimumFractionDigits: 1 })}</div>
                              </div>
                            ))}
                          </div>
                          <div className="text-[10px] text-neutral-500 mt-1">{k.dagen} opvangdagen dit jaar</div>
                        </div>
                      ))}
                      <div className="pt-3 mt-3 border-t border-neutral-100 flex items-center justify-between text-sm">
                        <span className="font-medium">Jaartotaal</span>
                        <span className="font-mono font-semibold">{kotData.totaal_uren.toLocaleString('nl-NL', { minimumFractionDigits: 2 })} uur</span>
                      </div>
                      <div className="text-[10px] text-neutral-500 mt-3 pt-2 border-t border-neutral-100">
                        {kotData.disclaimer} <a href="https://www.belastingdienst.nl/kinderopvangtoeslag" target="_blank" rel="noopener noreferrer" className="text-[color:var(--sage)] underline">Belastingdienst KOT</a>
                      </div>
                    </Card>
                  </div>
                )}

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Wallet className="h-4 w-4 text-[color:var(--sage)]" />
                    <h2 className="font-display text-xl tracking-tight">Facturen</h2>
                  </div>
                  <div className="space-y-2">
                    {invoices.length === 0 && <div className="text-sm text-neutral-500">Geen facturen.</div>}
                    {invoices.slice(0, 8).map(i => (
                      <Card key={i.id} className="p-3 bg-white shadow-none" data-testid={`portaal-factuur-${i.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-medium font-mono truncate">{i.invoice_number}</div>
                            <div className="text-xs text-neutral-500">
                              {i.kind_naam || "—"}
                              {i.kind_geboortedatum && <span className="text-neutral-400"> · geb. {i.kind_geboortedatum}</span>}
                            </div>
                            <div className="text-[11px] text-neutral-400">
                              {String(i.period_month || "").padStart(2, '0')}/{i.period_year || ""}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-mono text-sm">€ {(i.total ?? i.totaal ?? 0).toFixed(2)}</div>
                            <Badge variant="outline" className={`border text-[10px] ${STATUS_TONE[i.status] || "bg-neutral-100 text-neutral-700 border-neutral-200"}`}>
                              {i.betaald ? "Betaald" : i.overtijd ? "Overtijd" : i.status || "openstaand"}
                            </Badge>
                          </div>
                          <div className="flex flex-col gap-1">
                            <Button variant="ghost" size="sm" onClick={() => downloadPDF(i)} title="Download PDF" data-testid={`portaal-factuur-pdf-${i.id}`}>
                              <FileDown className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => downloadUBL(i)} title="Download UBL/XML" data-testid={`portaal-factuur-ubl-${i.id}`} className="text-[10px] text-neutral-500">
                              UBL
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <MessageSquare className="h-4 w-4 text-[color:var(--terracotta)]" />
                    <h2 className="font-display text-xl tracking-tight">Chat met de leidsters</h2>
                  </div>
                  <ChatOuderPanel messages={messages} kindId={selectedKid?.id} onSent={(m) => setMessages(prev => [...prev, m])} />
                </div>
              </div>
            </div>
          </>
        )}
        <div className="mt-16 pt-8 border-t border-neutral-200 text-center text-xs text-neutral-400">
          Powered by <span className="font-semibold text-[color:var(--sage)]">Webster</span>
        </div>
      </main>

      {/* Ziek/vakantie melden — ouder-flow */}
      {showSickDialog && selectedKid && (
        <SickReportDialog
          kind={selectedKid}
          onClose={() => setShowSickDialog(false)}
          onDone={() => {
            setShowSickDialog(false);
            // Refresh presentie
            api.get(`/portaal/kind/${selectedKid.id}/presentie`).then(r => {
              setPresentieToday(r.data);
            }).catch(() => {});
          }}
        />
      )}

      {/* Foto lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4"
          data-testid="portaal-photo-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white"
            data-testid="portaal-photo-lightbox-close"
            aria-label="Sluiten"
          >
            <X className="h-6 w-6" />
          </button>
          <div className="max-w-3xl max-h-full flex flex-col items-center gap-3" onClick={e => e.stopPropagation()}>
            <img
              src={`${BACKEND_ROOT}${lightbox.url}`}
              alt="foto"
              className="max-h-[80vh] max-w-full object-contain rounded-md"
            />
            <div className="text-center text-xs text-white/80">
              {new Date(lightbox.created_at).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              {lightbox.author_name && <span> · door {lightbox.author_name}</span>}
              {lightbox.caption && <div className="mt-1 text-white/70 italic">{lightbox.caption}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChatOuderPanel({ messages, kindId, onSent }) {
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [threadMessages, setThreadMessages] = useState([]);

  // Filter messages voor dit specifieke kind
  useEffect(() => {
    setThreadMessages((messages || []).filter(m => !kindId || m.kind_id === kindId));
  }, [messages, kindId]);

  // Real-time push van team via WebSocket
  useEffect(() => {
    if (!kindId) return;
    const handler = (e) => {
      const msg = e.detail?.message;
      if (msg && msg.kind_id === kindId) {
        setThreadMessages(prev => prev.some(x => x.id === msg.id) ? prev : [...prev, msg]);
      }
    };
    window.addEventListener("chat:new_message", handler);
    return () => window.removeEventListener("chat:new_message", handler);
  }, [kindId]);

  const readFile = (file) => new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });
  const pickPhotos = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4 - photos.length);
    for (const f of files) {
      if (f.size > 500_000) { toast.error(`${f.name} te groot (max ~500KB)`); continue; }
      try { const data = await readFile(f); setPhotos(p => [...p, data]); } catch { /* ignore */ }
    }
    e.target.value = "";
  };
  const send = async () => {
    if (!kindId) { toast.error("Kies eerst een kind"); return; }
    if (!text.trim() && photos.length === 0) return;
    setBusy(true);
    try {
      const r = await api.post("/mentorbericht", {
        kind_id: kindId,
        body: text.trim(),
        type: photos.length > 0 ? "activity" : "text",
        media: photos,
      });
      onSent?.(r.data);
      setThreadMessages(prev => [...prev, r.data]);
      setText(""); setPhotos([]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-neutral-200 rounded-lg bg-white flex flex-col min-h-[400px] max-h-[70vh]" data-testid="ouder-chat">
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {threadMessages.length === 0 && (
          <div className="text-center text-sm text-neutral-400 py-8">
            <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
            Nog geen berichten. De leidsters sturen updates over uw kind zodra er iets te melden is.
          </div>
        )}
        {threadMessages.map(m => <MessageBubble key={m.id} message={m} mineType="ouder" onDeleted={(mid) => setThreadMessages(prev => prev.filter(x => x.id !== mid))} />)}
      </div>
      <div className="border-t border-neutral-200 p-2.5 space-y-2">
        {photos.length > 0 && (
          <div className="flex gap-2 flex-wrap">
            {photos.map((p, i) => (
              <div key={i} className="relative">
                <img src={p} alt="" className="h-14 w-14 rounded object-cover border" />
                <button onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))}
                        className="absolute -top-1 -right-1 bg-white rounded-full border shadow p-0.5">
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <label className="cursor-pointer inline-flex items-center justify-center h-9 px-2 border border-neutral-200 rounded hover:bg-neutral-50" title="Foto (max 4)">
            <Camera className="h-4 w-4" />
            <input type="file" accept="image/*" multiple className="hidden" onChange={pickPhotos} data-testid="ouder-chat-photo" disabled={photos.length >= 4} />
          </label>
          <Textarea
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Bericht aan de leidsters…"
            rows={1}
            className="min-h-[38px] flex-1 resize-none text-sm"
            data-testid="ouder-chat-input"
          />
          <Button onClick={send} disabled={busy || (!text.trim() && photos.length === 0)}
                  data-testid="ouder-chat-send" style={{ background: "var(--terracotta)" }} className="text-white">
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}



function SickReportDialog({ kind, onClose, onDone }) {
  const [reden, setReden] = useState("ziek");
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [multiDay, setMultiDay] = useState(false);
  const rangeDays = (() => {
    try {
      const s = new Date(startDate); const e = new Date(endDate);
      const d = Math.round((e - s) / 86400000) + 1;
      return isFinite(d) && d > 0 ? d : 0;
    } catch { return 0; }
  })();
  const submit = async () => {
    if (multiDay && new Date(endDate) < new Date(startDate)) {
      toast.error("Einddatum ligt voor startdatum");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/portaal/kind/${kind.id}/afmelden-dag`, {
        reden,
        start_date: startDate,
        end_date: multiDay ? endDate : startDate,
        dagdelen: ["ochtend", "middag"],
      });
      const n = r.data?.dagen_afgemeld || 1;
      toast.success(
        n > 1
          ? `${kind.voornaam} is afgemeld voor ${n} dagen (${reden}). De opvang is geïnformeerd.`
          : `${kind.voornaam} is afgemeld voor ${startDate} (${reden}). De opvang is geïnformeerd.`
      );
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Afmelden mislukt"); }
    finally { setBusy(false); }
  };
  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="portaal-sick-dialog"
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-lg max-w-sm w-full p-5 space-y-3"
      >
        <div className="flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-amber-600" />
          <h3 className="font-semibold">Afmelden</h3>
        </div>
        <p className="text-sm text-neutral-600">
          Meld {kind.voornaam} af voor één dag óf een periode (bijv. vakantie).
        </p>
        <div>
          <label className="text-xs text-neutral-600">Reden</label>
          <select
            value={reden}
            onChange={e => setReden(e.target.value)}
            data-testid="portaal-sick-reden"
            className="w-full h-9 rounded-md border border-neutral-200 px-2 text-sm mt-1"
          >
            <option value="ziek">Ziek</option>
            <option value="vakantie">Vakantie</option>
            <option value="afwezig">Afwezig (anders)</option>
            <option value="doktersbezoek">Doktersbezoek</option>
          </select>
        </div>

        <label className="flex items-center gap-2 text-sm text-neutral-700">
          <input
            type="checkbox"
            checked={multiDay}
            onChange={e => setMultiDay(e.target.checked)}
            data-testid="portaal-sick-multiday"
            className="rounded border-neutral-300"
          />
          Meerdere dagen (vakantie/langere absentie)
        </label>

        {!multiDay ? (
          <div>
            <label className="text-xs text-neutral-600">Datum</label>
            <input
              type="date"
              value={startDate}
              onChange={e => { setStartDate(e.target.value); setEndDate(e.target.value); }}
              data-testid="portaal-sick-datum"
              className="w-full h-9 rounded-md border border-neutral-200 px-2 text-sm mt-1"
            />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-neutral-600">Van</label>
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                data-testid="portaal-sick-start"
                className="w-full h-9 rounded-md border border-neutral-200 px-2 text-sm mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-neutral-600">T/m</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={e => setEndDate(e.target.value)}
                data-testid="portaal-sick-end"
                className="w-full h-9 rounded-md border border-neutral-200 px-2 text-sm mt-1"
              />
            </div>
            {rangeDays > 0 && (
              <div className="col-span-2 text-[11px] text-neutral-500" data-testid="portaal-sick-range-summary">
                {rangeDays} {rangeDays === 1 ? "dag" : "dagen"} afmelden
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button
            onClick={submit}
            disabled={busy}
            data-testid="portaal-sick-submit"
            className="bg-amber-600 hover:bg-amber-700 text-white"
          >
            {busy ? "Bezig…" : "Afmelden"}
          </Button>
        </div>
      </div>
    </div>
  );
}

